/**
 * Shared Trainee form dialog + dynamic-field input + cell value renderer.
 *
 * Extracted from TraineesTab.jsx (June 28 2026) so the new BatchDetailPage
 * can reuse the same authoring surface without dragging the whole tab into
 * the route. Behaviour is byte-for-byte identical to the in-tab version —
 * only the export surface changed.
 *
 * `lockBatchId`: when set, the Batch select is hidden and the trainee is
 * filed into that batch unconditionally. Used by BatchDetailPage so trainers
 * adding a trainee from inside a batch never have to pick a batch.
 */
import { useEffect, useMemo, useState } from 'react';
import { UserCheck, AlertTriangle } from 'lucide-react';
import api from '../../api/client';

import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const NO_BATCH = '__none__';
const ALL_DEPTS = '__all__';
const SHOW_ALL_KEY = 'lumyn.editTrainee.showAllAssessments';

const hasScore = (v) => !(v === null || v === undefined || v === '');

export function renderFieldValue(field, value) {
  if (value === null || value === undefined || value === '') return <span className="text-muted-foreground">—</span>;
  if (Array.isArray(value)) return value.join(', ');
  if (field.field_type === 'percentage') return `${value}%`;
  if (field.field_key === 'alert_status') {
    const color = value === 'Red' ? 'bg-rose-100 text-rose-700'
                : value === 'Amber' ? 'bg-amber-100 text-amber-700'
                : value === 'Green' ? 'bg-emerald-100 text-emerald-700'
                : 'bg-slate-100 text-slate-700';
    return <Badge variant="outline" className={color}>{value}</Badge>;
  }
  return String(value);
}

export function TraineeFormDialog({ open, onOpenChange, editing, form, setForm, batches, fields, onSave, lockBatchId = null, departments = [] }) {
  const visibleFields = fields.filter(f => f.is_visible && f.field_type !== 'calculated');

  // Live duplicate check (add mode only): same Cisco ID / email anywhere in the org
  const [match, setMatch] = useState(null);
  const targetBatch = lockBatchId || form.batch_id || '';
  useEffect(() => {
    if (editing || !open) { setMatch(null); return; }
    const email = (form.email || '').trim();
    const emp = (form.employee_id || '').trim();
    if (!email && !emp) { setMatch(null); return; }
    let live = true;
    const h = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (email) params.set('email', email);
        if (emp) params.set('employee_id', emp);
        if (targetBatch) params.set('batch_id', targetBatch);
        const r = await api.get(`/trainees/lookup?${params.toString()}`);
        if (live) setMatch(r.data.match);
      } catch { if (live) setMatch(null); }
    }, 350);
    return () => { live = false; clearTimeout(h); };
  }, [form.email, form.employee_id, targetBatch, editing, open]);
  const blocked = !!match && (match.in_this_batch || !targetBatch);

  // Department filter for the Batch picker — defaults to the dept of the
  // currently-selected batch (so edit flows pre-filter correctly), and
  // falls back to "All" otherwise.
  const [deptFilter, setDeptFilter] = useState(ALL_DEPTS);

  useEffect(() => {
    if (!open) return;
    if (form.batch_id) {
      const b = batches.find(x => x.batch_id === form.batch_id);
      setDeptFilter(b?.department_id || ALL_DEPTS);
    } else {
      setDeptFilter(ALL_DEPTS);
    }
    // We only want this to re-sync on dialog open / batch change, not on
    // every parent re-render.
  }, [open, form.batch_id]);

  const filteredBatches = useMemo(() => {
    if (deptFilter === ALL_DEPTS) return batches;
    return batches.filter(b => b.department_id === deptFilter);
  }, [batches, deptFilter]);

  const onDeptChange = (v) => {
    setDeptFilter(v);
    // If the currently picked batch doesn't belong to the new dept, clear it.
    if (v !== ALL_DEPTS && form.batch_id) {
      const b = batches.find(x => x.batch_id === form.batch_id);
      if (b && b.department_id !== v) setForm({ ...form, batch_id: '' });
    }
  };

  const updateField = (key, value) => {
    setForm({ ...form, fields: { ...form.fields, [key]: value } });
  };

  // Edit mode: hide assessment score fields this trainee never attempted.
  const [showAllAssessments, setShowAllAssessments] = useState(
    () => sessionStorage.getItem(SHOW_ALL_KEY) === '1');
  const toggleShowAll = (v) => {
    setShowAllAssessments(v);
    sessionStorage.setItem(SHOW_ALL_KEY, v ? '1' : '0');
  };
  const sectionItems = (section) => {
    const items = visibleFields.filter(f => f.section === section
      && !['name_of_trainee', 'cisco_id', 'email_id'].includes(f.field_key));
    if (section !== 'performance' || !editing) return { items, hidden: 0, total: 0 };
    const scores = items.filter(f => f.is_assessment_score);
    const attempted = scores.filter(f => hasScore((form.fields || {})[f.field_key]));
    const hidden = scores.length - attempted.length;
    if (showAllAssessments) return { items, hidden, total: scores.length };
    return {
      items: items.filter(f => !f.is_assessment_score || hasScore((form.fields || {})[f.field_key])),
      hidden, total: scores.length,
    };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="trainee-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Trainee' : 'Add Trainee'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                     data-testid="trainee-input-name" />
            </div>
            <div>
              <Label>Cisco ID / Employee ID</Label>
              <Input value={form.employee_id}
                     onChange={e => setForm({ ...form, employee_id: e.target.value })}
                     data-testid="trainee-input-employee-id" />
            </div>
          </div>
          {match && (
            <div className={`rounded-md border p-2 text-sm flex gap-2 ${blocked ? 'border-rose-300 bg-rose-50 text-rose-800' : 'border-emerald-300 bg-emerald-50 text-emerald-900'}`}
                 data-testid="trainee-duplicate-notice">
              {blocked ? <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> : <UserCheck className="w-4 h-4 mt-0.5 shrink-0" />}
              <span>
                <strong>{match.name}</strong> ({match.employee_id || match.email}) already exists
                {match.batch_name ? <> in <strong>{match.batch_name}</strong></> : null}.
                {blocked
                  ? ' They are already on this roster — edit that record instead of adding a duplicate.'
                  : ' Saving will enrol them in this batch instead of creating a duplicate — earlier record kept, status starts clean.'}
              </span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email}
                     onChange={e => setForm({ ...form, email: e.target.value })}
                     data-testid="trainee-input-email" />
            </div>
            {!lockBatchId && (
              <div>
                <Label>Batch</Label>
                {departments.length > 0 && (
                  <Select value={deptFilter} onValueChange={onDeptChange}>
                    <SelectTrigger className="h-8 mb-1.5" data-testid="trainee-input-department">
                      <SelectValue placeholder="All departments" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_DEPTS}>All departments</SelectItem>
                      {departments.filter(d => d.is_active).map(d => (
                        <SelectItem key={d.department_id} value={d.department_id}>
                          {d.code} — {d.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Select value={form.batch_id || NO_BATCH}
                        onValueChange={v => setForm({ ...form, batch_id: v === NO_BATCH ? '' : v })}>
                  <SelectTrigger data-testid="trainee-input-batch"><SelectValue placeholder="No batch" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_BATCH}>No batch</SelectItem>
                    {filteredBatches.map(b => (
                      <SelectItem key={b.batch_id} value={b.batch_id}>
                        {b.name}{b.code ? ` · ${b.code}` : ''}
                      </SelectItem>
                    ))}
                    {filteredBatches.length === 0 && (
                      <div className="px-2 py-1.5 text-xs text-muted-foreground">
                        No batches in this department
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Dynamic fields by section */}
          {['basic', 'status', 'performance', 'custom'].map(section => {
            const { items, hidden, total } = sectionItems(section);
            const showToggle = section === 'performance' && editing && total > 0;
            if (items.length === 0 && !showToggle) return null;
            const sectionLabel = section.charAt(0).toUpperCase() + section.slice(1);
            return (
              <div key={section} className="space-y-2 pt-2 border-t">
                <div className="flex items-center justify-between gap-3">
                  <h4 className="font-display text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    {sectionLabel}
                  </h4>
                  {showToggle && (
                    <label className="flex items-center gap-2 text-xs font-display cursor-pointer"
                           data-testid="show-all-assessments-toggle">
                      <span className="text-muted-foreground">Show all assessments</span>
                      <Switch checked={showAllAssessments} onCheckedChange={toggleShowAll}
                              className="scale-90" data-testid="show-all-assessments-switch" />
                    </label>
                  )}
                </div>
                {showToggle && hidden > 0 && (
                  <p className="text-xs text-muted-foreground -mt-1" data-testid="hidden-assessments-note">
                    {showAllAssessments
                      ? `${hidden} of ${total} assessments have no score recorded`
                      : `${hidden} of ${total} assessments hidden (no score recorded)`}
                  </p>
                )}
                <div className="grid grid-cols-2 gap-3">
                  {items.map(f => (
                    <DynamicFieldInput
                      key={f.field_id}
                      field={f}
                      value={(form.fields || {})[f.field_key]}
                      onChange={(v) => updateField(f.field_key, v)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={blocked} data-testid="trainee-save-btn">
            {editing ? 'Update' : (match ? 'Enrol existing' : 'Add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DynamicFieldInput({ field, value, onChange }) {
  const v = value ?? '';
  const testId = `field-input-${field.field_key}`;
  switch (field.field_type) {
    case 'numeric':
    case 'percentage':
    case 'score':
      return (
        <div>
          <Label>{field.label}{field.is_required && ' *'}</Label>
          <Input type="number" value={v} data-testid={testId}
                 min={field.min_value ?? undefined} max={field.max_value ?? undefined}
                 onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))} />
        </div>
      );
    case 'email':
      return (
        <div>
          <Label>{field.label}{field.is_required && ' *'}</Label>
          <Input type="email" value={v} data-testid={testId}
                 onChange={e => onChange(e.target.value)} />
        </div>
      );
    case 'date':
      return (
        <div>
          <Label>{field.label}{field.is_required && ' *'}</Label>
          <Input type="date" value={typeof v === 'string' ? v.slice(0, 10) : ''}
                 data-testid={testId}
                 onChange={e => onChange(e.target.value)} />
        </div>
      );
    case 'dropdown':
    case 'radio':
      return (
        <div>
          <Label>{field.label}{field.is_required && ' *'}</Label>
          <Select value={v || '__none__'}
                  onValueChange={x => onChange(x === '__none__' ? null : x)}>
            <SelectTrigger data-testid={testId}><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">—</SelectItem>
              {(field.options || []).map(opt => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    case 'multiselect': {
      const selected = Array.isArray(value) ? value : [];
      return (
        <div>
          <Label>{field.label}{field.is_required && ' *'}</Label>
          <Textarea
            rows={2}
            value={selected.join(', ')}
            data-testid={testId}
            placeholder={`Comma-separated: ${(field.options || []).slice(0, 3).join(', ')}`}
            onChange={e => onChange(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
          />
        </div>
      );
    }
    case 'checkbox':
      return (
        <div className="flex items-center justify-between pt-6">
          <Label>{field.label}{field.is_required && ' *'}</Label>
          <input type="checkbox" checked={!!v} data-testid={testId}
                 onChange={e => onChange(e.target.checked)} className="w-4 h-4" />
        </div>
      );
    default:
      return (
        <div>
          <Label>{field.label}{field.is_required && ' *'}</Label>
          <Input value={v} data-testid={testId} placeholder={field.placeholder || ''}
                 onChange={e => onChange(e.target.value)} />
        </div>
      );
  }
}
